import type { Dayjs } from 'dayjs';
import capitalize from 'lodash.capitalize';
import { useState, type FC, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { formatNumber } from '../lib/number';
import { Accordion, SearchBox, TooltipElement, Typography } from '../ui';
import { ExportXlsxButton } from './export-xlsx-button';

const ACCORDION_ID = 'filter-section-accordion';

type FilterSectionProps = {
  form: ReactNode;
  title: string;
  counterLabel?: { singular: string; plural: string };
  dateRange?: {
    from: Dayjs;
    to: Dayjs;
  };
  itemsCount?: {
    filtered: number;
    total: number;
  };
  bottomAddon?: ReactNode;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  loading?: boolean;
  hideTooltip?: boolean;
  getFiltersLabel: () => string;
  onExport?: () => void;
  additionalActions?: ReactNode;
};

export const FilterSection: FC<FilterSectionProps> = ({
  title,
  counterLabel,
  bottomAddon,
  itemsCount,
  dateRange,
  form,
  loading,
  search,
  hideTooltip = false,
  getFiltersLabel,
  onExport,
  additionalActions,
}) => {
  const [isOpened, setIsOpened] = useState(false);

  const getCleanLabel = (count?: number | null) => {
    const base = title.toLowerCase();
    const raw = count === 1 ? base : `${base}s`;
    // remove duplicated 's' occurrences at word end (e.g. 'facilitiess' -> 'facilities')
    return raw.replace(/s{2,}(?=\s|$)/g, 's');
  };

  return (
    <div className="border-neutral-5 bg-neutral-1 flex flex-col border-x border-t">
      <Accordion.Root
        collapsible
        type="single"
        value={isOpened ? ACCORDION_ID : ''}
        onValueChange={(value) => {
          setIsOpened(value === ACCORDION_ID);
        }}
      >
        <Accordion.Item value={ACCORDION_ID}>
          <Accordion.Trigger
            className="p-3 text-left"
            triggerButton={{
              variant: 'secondary',
              size: 'small',
            }}
          >
            <div className="flex w-full min-w-0 items-center justify-between">
              <Typography
                as="h3"
                variant="h3"
                weight="medium"
                className="truncate"
              >
                {title}
              </Typography>

              <Typography
                as="h5"
                variant="h5"
                weight="medium"
                className="mx-3 max-w-1/2 shrink-0 truncate"
              >
                {'Filters '}

                {!isOpened && (
                  <Typography as="span" className="text-primary-text">
                    {getFiltersLabel()}
                  </Typography>
                )}
              </Typography>
            </div>
          </Accordion.Trigger>
          <Accordion.Content>{form}</Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>

      <div className="border-neutral-5 flex w-full items-center border-t p-3">
        <div className="flex flex-1 items-center gap-4">
          {bottomAddon}
          <Typography className="text-neutral-9">
            {!itemsCount && '—'}
            {itemsCount && (
              <Typography as="span">
                {formatNumber(itemsCount.filtered)}{' '}
                {counterLabel
                  ? itemsCount.filtered === 1
                    ? counterLabel.singular
                    : counterLabel.plural
                  : getCleanLabel(itemsCount.filtered)}{' '}
                of {formatNumber(itemsCount.total)}
              </Typography>
            )}
          </Typography>
        </div>

        <div
          className={cn(
            'flex items-center',
            search || onExport || additionalActions
              ? 'flex-none justify-center'
              : 'flex-1 justify-end'
          )}
        >
          {dateRange ? (
            <Typography noWrap className="text-neutral-9">
              {dateRange.from.format('MMM D, YYYY')} {'– '}
              {dateRange.to.format('MMM D, YYYY')}
            </Typography>
          ) : (
            <div className="flex items-center gap-x-2">
              <Typography noWrap className="text-neutral-9">
                Current Status
              </Typography>
              {!hideTooltip && (
                <TooltipElement content="This table always shows the most up-to-date data and is not affected by the date filter" />
              )}
            </div>
          )}
        </div>

        {(search || onExport || additionalActions) && (
          <div className="flex flex-[1.2] items-center justify-end gap-4">
            {search && (
              <SearchBox
                iconName="search"
                className="w-[200px]"
                bottomGutter={false}
                placeholder={
                  search.placeholder ??
                  `Search ${capitalize(title.toLowerCase())}`
                }
                loading={loading}
                onChange={search.onChange}
              />
            )}

            {additionalActions}
            {onExport && <ExportXlsxButton onExport={onExport} />}
          </div>
        )}
      </div>
    </div>
  );
};
